import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { getLeaderDashboardData, getUsers, getPosters, getPosterCategories, getCommissionCalculation, getCommissionSettings, getImageUrl, API_BASE_URL, getMutualFundDashboardData, getGeneralInsuranceDashboardData } from '../model/api.js';

import Table from './components/common/Table.jsx';
import LoadingSpinner from './components/common/LoadingSpinner.jsx';
import GeneralInsuranceList from './GeneralInsuranceList.jsx';
import { MutualFundTab } from './components/MutualFundTab.jsx';
import ProfileTab from './components/ProfileTab.jsx';
import DashboardStats from './components/DashboardStats.jsx';
import CreatePolicyModal from './components/common/CreatePolicyModal.jsx';

const LeaderDashboard = ({ user, refreshUser }) => {
  // Global Filter State
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  // Data States
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // Health Data (default dashboard)
  const [mfDashboardData, setMfDashboardData] = useState(null);
  const [giDashboardData, setGiDashboardData] = useState(null);
  const [chartData, setChartData] = useState([]); // Kept if needed for logic, but graphs removed

  // Tab State
  const [activeTab, setActiveTab] = useState('dashboard');

  // Health Tab Sub-states
  const [healthSubTab, setHealthSubTab] = useState('advisor'); // 'advisor' | 'leader'
  const [healthSearch, setHealthSearch] = useState('');

  // Posters State
  const [posters, setPosters] = useState([]);
  const [posterCategories, setPosterCategories] = useState([]);
  const [posterLoading, setPosterLoading] = useState(false);
  const [selectedPosterCategory, setSelectedPosterCategory] = useState('all');
  const [posterViewMode, setPosterViewMode] = useState('grid');
  const [downloadingPosterId, setDownloadingPosterId] = useState(null);

  // Commission Detail State
  const [detailedCalculation, setDetailedCalculation] = useState(null);
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [uplineRate, setUplineRate] = useState(0.1);
  const [activationThreshold, setActivationThreshold] = useState(15000);

  // Modal State
  const [showCreatePolicyModal, setShowCreatePolicyModal] = useState(false);
  const [usersData, setUsersData] = useState([]);

  const [myLeaderData, setMyLeaderData] = useState(null);
  const [showCommissionModal, setShowCommissionModal] = useState(false);

  // Filter Constants
  const currentYear = new Date().getFullYear();
  const startYear = 2025;
  const endYear = Math.max(2036, currentYear + 10);
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);
  const months = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' },
    { value: '03', label: 'March' }, { value: '04', label: 'April' },
    { value: '05', label: 'May' }, { value: '06', label: 'June' },
    { value: '07', label: 'July' }, { value: '08', label: 'August' },
    { value: '09', label: 'September' }, { value: '10', label: 'October' },
    { value: '11', label: 'November' }, { value: '12', label: 'December' }
  ];

  // Helper: Handlers
  const handleYearChange = (e) => {
    const [_, month] = selectedMonth.split('-');
    const newMonth = `${e.target.value}-${month}`;
    setSelectedMonth(newMonth);
  };

  const handleMonthSelectChange = (e) => {
    const [year, _] = selectedMonth.split('-');
    const newMonth = `${year}-${e.target.value}`;
    setSelectedMonth(newMonth);
  };

  // Helper: Icons
  const DownloadIcon = ({ className }) => <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>;

  // --- Data Fetching ---

  const fetchUsersData = useCallback(async () => {
    try {
      const result = await getUsers({ limit: 2000 }); // Fetch enough to find leader
      const users = result.data?.users || result.users || result || [];

      // Filter for 'My Advisors' (subordinates)
      const subordinates = users.filter(u =>
        u.status === 'active' &&
        (u.role === 'advisor' || u.role === 'leader') &&
        (u.leader_id === user.id)
      );
      setUsersData(subordinates);

      // Find 'My Leader'
      if (user.leader_id) {
        const leader = users.find(u => u._id === user.leader_id);
        setMyLeaderData(leader || null);
      }
    } catch (error) {
      console.error("Failed to fetch users data", error);
      setUsersData([]);
    }
  }, [user.id, user.leader_id]);

  const fetchDetailedCalculation = useCallback(async (commissionId) => {
    if (!commissionId) {
      setDetailedCalculation(null);
      return;
    }
    console.log('[DEBUG] Fetching detailed calculation for commission ID:', commissionId);
    setCalculationLoading(true);
    try {
      const result = await getCommissionCalculation(commissionId);
      console.log('[DEBUG] Detailed calculation result:', result);
      setDetailedCalculation(result);
    } catch (error) {
      console.error("Failed to fetch detailed calculation", error);
      setDetailedCalculation(null);
    } finally {
      setCalculationLoading(false);
    }
  }, []);

  const fetchData = useCallback(async (month = selectedMonth) => {
    if (!user || !user.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [year, monthNum] = month.split('-');
      const result = await getLeaderDashboardData(user.id, parseInt(monthNum), parseInt(year));
      setData(result);

      // Fetch detailed calc if available
      if (result.commissionBreakdown?.commissionId) {
        await fetchDetailedCalculation(result.commissionBreakdown.commissionId);
      } else {
        setDetailedCalculation(null);
      }
    } catch (error) {
      console.error("Failed to fetch leader data", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id, selectedMonth, fetchDetailedCalculation]);

  const fetchPosters = useCallback(async () => {
    setPosterLoading(true);
    try {
      const fetchParams = {
        category: selectedPosterCategory === 'all' ? undefined : selectedPosterCategory,
        limit: 50
      };
      const result = await getPosters(fetchParams);
      if (result.ok && result.data) {
        setPosters(result.data.posters || []);
      } else {
        setPosters([]);
      }
    } catch (error) {
      console.error('Error fetching posters:', error);
      setPosters([]);
    } finally {
      setPosterLoading(false);
    }
  }, [selectedPosterCategory]);

  const fetchPosterCategories = useCallback(async () => {
    try {
      const result = await getPosterCategories();
      if (result.ok && result.data) setPosterCategories(result.data);
    } catch (error) {
      console.error('Error fetching poster categories:', error);
    }
  }, []);

  const isNewPoster = (createdAt) => {
    if (!createdAt) return false;
    const now = new Date();
    const createdDate = new Date(createdAt);
    const diffInHours = (now - createdDate) / (1000 * 60 * 60);
    return diffInHours <= 168; // 7 days (7 * 24 = 168 hours)
  };

  const downloadPersonalizedPoster = async (posterId, posterName, userName) => {
    setDownloadingPosterId(posterId);
    try {
      const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
      };

      const token = getCookie('token');
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/posters/${posterId}/download-personalized`, {
        headers
      });

      if (!response.ok) {
        // Safely parse error - handle both JSON and non-JSON responses
        let errorMessage = `Failed to download poster (${response.status})`;
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } else {
            const text = await response.text();
            errorMessage = text || errorMessage;
          }
        } catch (parseErr) {
          console.warn('Could not parse error response:', parseErr);
        }
        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      if (blob.size === 0) throw new Error('Downloaded file is empty');
      if (blob.type === 'application/json' || blob.type.includes('json')) {
        const text = await blob.text();
        let errorMessage = 'Server returned an error';
        try {
          const errorData = JSON.parse(text);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = text || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${posterName}_${userName}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Poster downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Download failed: ${error.message}`);
    } finally {
      setDownloadingPosterId(null);
    }
  };

  // Initial Fetch
  useEffect(() => {
    fetchUsersData();
    // Get Upline Rate
    getCommissionSettings().then(res => {
      const settings = res.data || res;
      const rate = settings?.uplineConfig?.find(c => c.name === 'Direct Upline')?.rate || 0.1;
      setUplineRate(rate);
      if (settings?.minPremiumThreshold) {
        setActivationThreshold(settings.minPremiumThreshold);
      }
    }).catch(err => console.error(err));
  }, [fetchUsersData]);

  // Fetch based on active tab & selectedMonth
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchData();
    } else if (activeTab === 'mutual') {
      const [year, monthNum] = selectedMonth.split('-');
      getMutualFundDashboardData({ month: monthNum, year: year }).then(res => {
        if (res.ok) setMfDashboardData(res.data);
      });
    } else if (activeTab === 'general') {
      const [year, monthNum] = selectedMonth.split('-');
      getGeneralInsuranceDashboardData({ month: monthNum, year: year }).then(res => {
        if (res.ok) setGiDashboardData(res.data);
      });
    } else if (activeTab === 'posters') {
      fetchPosters();
      fetchPosterCategories();
    }
  }, [activeTab, selectedMonth, fetchData, fetchPosters, fetchPosterCategories]);


  // Columns Definitions
  const healthAdvisorColumns = [
    { header: 'Name', accessor: (item) => item.advisorName || item.name },
    { header: 'Role', accessor: (item) => <span className="capitalize">{item.role}</span> },
    { header: 'Phone', accessor: (item) => item.phone || 'N/A' },
    { header: 'WealthPlus ID', accessor: (item) => item.wealthplus_id || item.tata_id || 'N/A' },
    { header: 'Policies', accessor: (item) => item.policiesCount || 0 },
    { header: 'Revenue', accessor: (item) => `₹${(item.monthlyRevenue || 0).toLocaleString('en-IN')}` },
    {
      header: 'Status',
      accessor: (item) => (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${(item.monthlyRevenue || 0) >= activationThreshold ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
          {(item.monthlyRevenue || 0) >= activationThreshold ? 'Active' : 'Inactive'}
        </span>
      )
    },
  ];

  // Columns for My Leader tab
  const myLeaderColumns = [
    { header: 'Name', accessor: (item) => item.advisorName || item.name },
    { header: 'Role', accessor: (item) => <span className="capitalize">{item.role}</span> },
    { header: 'Phone', accessor: (item) => item.phone || 'N/A' },
    { header: 'WealthPlus ID', accessor: (item) => item.wealthplus_id || item.tata_id || 'N/A' },
    { header: 'Their Commission', accessor: (item) => `₹${(item.monthlyCommission || 0).toLocaleString('en-IN')}` },
    {
      header: 'Status',
      accessor: (item) => (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${(item.monthlyRevenue || 0) >= activationThreshold ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
          {(item.monthlyRevenue || 0) >= activationThreshold ? 'Active' : 'Inactive'}
        </span>
      )
    },
  ];

  const teamPolicyColumns = [
    { header: 'Advisor Name', accessor: 'advisorName' },
    { header: 'Policy No', accessor: 'policyNo' },
    { header: 'Premium', accessor: (item) => `₹${(item.premium || 0).toLocaleString('en-IN')}` },
    { header: 'Start Date', accessor: (item) => item.startDate ? new Date(item.startDate).toLocaleDateString('en-GB') : 'N/A' },
  ];

  /* ----------------------------------------------------------------------------------
   * RENDER HELPERS
   * ---------------------------------------------------------------------------------- */

  const renderHealthTab = () => {
    // Determine data source to display
    // We want to use 'data.teamMembers' for stats if possible, merged with filter logic.
    // 'data.teamMembers' has monthly performance. 'usersData' has static info.
    // If we use 'data.teamMembers', we get revenue. This is what we want.
    let displayData = [];

    if (healthSubTab === 'advisor') {
      // Filter 'data.teamMembers' for subordinates
      // data.teamMembers usually comes from 'getLeaderDashboardData'
      if (data?.teamMembers) {
        displayData = data.teamMembers.filter(m => m.role === 'advisor' && m._id !== user.id);
      }
    } else if (healthSubTab === 'leader') {
      // 'My Leader' - show subordinate leaders (leaders who report to this user)
      // Filter team members for leaders only
      if (data?.teamMembers) {
        displayData = data.teamMembers.filter(m => m.role === 'leader' && m._id !== user.id);
      }
    }

    // Filter by search
    if (healthSearch) {
      const lowerSearch = healthSearch.toLowerCase();
      displayData = displayData.filter(u =>
        (u.advisorName || u.name || '').toLowerCase().includes(lowerSearch) ||
        (u.email || '').toLowerCase().includes(lowerSearch)
      );
    }

    // Sort: Active (revenue > activationThreshold) first
    displayData.sort((a, b) => {
      const revA = a.monthlyRevenue || 0;
      const revB = b.monthlyRevenue || 0;
      const activeA = revA >= activationThreshold;
      const activeB = revB >= activationThreshold;
      if (activeA === activeB) return 0;
      return activeA ? -1 : 1;
    });

    return (
      <div className="space-y-6">
        {/* Sub-tabs */}
        {/* Commission Breakdown (Only for Advisor view - or main view) */}
        {data?.commissionBreakdown && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Commission Breakdown</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center mb-6">
              <div>
                <p className="text-sm text-gray-500">Commission Revenue</p>
                <p className="text-xs text-gray-400">(All Policy Years)</p>
                <p className="text-2xl font-bold text-gray-900">₹{(data.commissionBreakdown.totalRevenue || 0).toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Commission Rate</p>
                <p className="text-2xl font-bold text-blue-600">{((data.commissionBreakdown.commissionRate || 0) * 100).toFixed(1)}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Commission</p>
                <p className="text-2xl font-bold text-green-600">₹{(data.commissionBreakdown.totalCommission || 0).toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <div className="flex flex-col items-center">
                  <p className={`text-2xl font-bold ${data.commissionBreakdown.status === 'paid' ? 'text-green-600' : 'text-red-500'}`}>
                    {data.commissionBreakdown.status === 'paid' ? 'PAID' : 'UNPAID'}
                  </p>
                  {data.commissionBreakdown.status === 'paid' && (
                    <div className="mt-2 text-xs text-gray-600 text-center">
                      {(data.commissionBreakdown.transaction_ref || data.commissionBreakdown.transaction_id || data.commissionBreakdown.payout_ref) ? (
                        <p className="font-medium text-indigo-600">
                          TXN: {data.commissionBreakdown.transaction_ref || data.commissionBreakdown.transaction_id || data.commissionBreakdown.payout_ref}
                        </p>
                      ) : (
                        <p className="text-red-400 italic font-medium">TNX ID Missing</p>
                      )}
                      {data.commissionBreakdown.paid_at && (
                        <p className="text-gray-500">
                          {new Date(data.commissionBreakdown.paid_at).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sub-tabs */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-4 rounded-lg shadow-sm">
          <div className="flex space-x-2">
            <button
              onClick={() => setHealthSubTab('advisor')}
              className={`px-4 py-2 rounded-md transition-colors ${healthSubTab === 'advisor' ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              My Advisors
            </button>
            <button
              onClick={() => setHealthSubTab('leader')}
              className={`px-4 py-2 rounded-md transition-colors ${healthSubTab === 'leader' ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              My Leader
            </button>
            <button
              onClick={() => {
                setHealthSubTab('commission');
                if (data?.commissionBreakdown?.commissionId) {
                  fetchDetailedCalculation(data.commissionBreakdown.commissionId);
                }
              }}
              className={`px-4 py-2 rounded-md transition-colors ${healthSubTab === 'commission' ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              Commission
            </button>
          </div>
        </div>

        {/* Commission Sub-tab Content */}
        {healthSubTab === 'commission' && (
          <div className="animate-fade-in space-y-6">
            {calculationLoading ? (
              <LoadingSpinner />
            ) : detailedCalculation ? (
              <div className="space-y-6">

                {/* Blue Section Header */}
                <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
                  <h4 className="text-blue-700 font-semibold text-lg">Direct Team Business Commission</h4>
                </div>

                {/* Green Section: Year-by-Year Breakdown */}
                <div className="bg-green-50 rounded-lg p-6 border border-green-100">
                  <h5 className="text-green-800 font-semibold mb-4">Year-by-Year Commission Breakdown</h5>

                  {detailedCalculation.calculations && detailedCalculation.calculations.length > 0 ? (
                    <div className="overflow-x-auto bg-white rounded-md shadow-sm">
                      <table className="min-w-full text-sm text-left">
                        <thead className="bg-green-100 text-green-800 uppercase text-xs font-semibold">
                          <tr>
                            <th className="px-6 py-3">Year</th>
                            <th className="px-6 py-3">Revenue</th>
                            <th className="px-6 py-3">Rate</th>
                            <th className="px-6 py-3 text-right">Commission</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {detailedCalculation.calculations.map((calc, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-6 py-4 font-medium text-gray-900">{calc.year}</td>
                              <td className="px-6 py-4 text-gray-600">₹{calc.revenue.toLocaleString('en-IN')}</td>
                              <td className="px-6 py-4 text-gray-600">
                                {calc.rate}
                              </td>
                              <td className="px-6 py-4 text-right font-semibold text-green-600">
                                ₹{calc.commission.toLocaleString('en-IN')}
                              </td>
                            </tr>
                          ))}
                          <tr className="bg-green-50 font-bold border-t border-green-200">
                            <td className="px-6 py-4 text-green-900">Total</td>
                            <td className="px-6 py-4 text-green-900">
                              ₹{(detailedCalculation.calculations?.reduce((sum, calc) => sum + (calc.revenue || 0), 0) || 0).toLocaleString('en-IN')}
                            </td>
                            <td className="px-6 py-4"></td>
                            <td className="px-6 py-4 text-right text-green-800 text-lg">
                              ₹{(detailedCalculation.calculations?.reduce((sum, calc) => sum + (calc.commission || 0), 0) || 0).toLocaleString('en-IN')}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No yearly breakdown available.</p>
                  )}
                </div>

                {/* Downline Leader Commission Section */}
                <div className="bg-purple-50 rounded-lg p-6 border border-purple-100">
                  <h5 className="text-purple-800 font-semibold mb-4">Downline Leader Commission</h5>
                  <div className="overflow-x-auto bg-white rounded-md shadow-sm">
                    <table className="min-w-full text-sm text-left">
                      <thead className="bg-purple-100 text-purple-800 uppercase text-xs font-semibold">
                        <tr>
                          <th className="px-6 py-3">Description</th>
                          <th className="px-6 py-3 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        <tr className="bg-white">
                          <td className="px-6 py-4 text-gray-700">Share from Subordinate Leaders</td>
                          <td className="px-6 py-4 text-right font-semibold text-purple-600">
                            ₹{(() => {
                              const total = detailedCalculation.totalCommission || 0;
                              const sum = detailedCalculation.calculations?.reduce((s, c) => s + (c.commission || 0), 0) || 0;
                              const diff = total - sum;
                              // Precision Fix: If difference is less than 1 paisa (0.01), treat as 0 to avoid artifacts like -0.003
                              return (Math.abs(diff) < 0.01 ? 0 : diff).toLocaleString('en-IN');
                            })()}
                          </td>
                        </tr>
                        <tr className="bg-purple-50 font-bold border-t border-purple-200">
                          <td className="px-6 py-4 text-purple-900">Total Downline Commission</td>
                          <td className="px-6 py-4 text-right text-purple-800 text-lg">
                            ₹{(() => {
                              const total = detailedCalculation.totalCommission || 0;
                              const sum = detailedCalculation.calculations?.reduce((s, c) => s + (c.commission || 0), 0) || 0;
                              const diff = total - sum;
                              return (Math.abs(diff) < 0.01 ? 0 : diff).toLocaleString('en-IN');
                            })()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Grand Total Footer */}
                <div className="bg-green-50 rounded-lg border border-green-200 p-6 flex justify-between items-center">
                  <div>
                    <h5 className="text-lg font-bold text-green-800">Grand Total Commission</h5>
                    <p className="text-xs text-green-600 mt-1">Direct Team Commission + Downline Leader Share</p>
                  </div>
                  <p className="text-3xl font-bold text-green-700">₹{detailedCalculation.totalCommission?.toLocaleString('en-IN')}</p>
                </div>

              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <p className="text-gray-500 text-lg">No detailed commission calculation found for this month.</p>
                <p className="text-gray-400 text-sm mt-2">Ensure commission has been calculated by the system.</p>
              </div>
            )}
          </div>
        )}

        {/* List (Advisors or Leader) */}
        {healthSubTab !== 'commission' && (
          <Table
            columns={healthSubTab === 'leader' ? myLeaderColumns : healthAdvisorColumns}
            data={displayData}
            title={healthSubTab === 'advisor' ? `My Advisors (${data?.currentMonth || selectedMonth})` : 'My Leader'}
            actions={
              <div className="w-full md:w-64">
                <input
                  type="text"
                  placeholder="Search by Name or Email"
                  value={healthSearch}
                  onChange={(e) => setHealthSearch(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            }
          />
        )}

        {/* Team Policies List */}
        {healthSubTab === 'advisor' && displayData.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Team Policies</h3>
            <Table
              columns={teamPolicyColumns}
              data={displayData.flatMap(advisor =>
                (advisor.policies || []).map(p => ({
                  ...p,
                  advisorName: advisor.advisorName || advisor.name,
                  _id: p.policyNo // Unique key
                }))
              )}
            // No separate search/filter for this table yet, it inherits advisor filter indirectly
            />
          </div>
        )}


      </div>
    );
  };

  const renderGeneralInsuranceTab = () => {
    if (!giDashboardData) return <LoadingSpinner />;

    // Calculate Grand Total: Team Override + Downline Share + Personal Commission
    const directTeamEarnings = giDashboardData.summary.directTeam?.myEarnings || 0;
    const downlineShare = giDashboardData.summary.downlineLeaders ? giDashboardData.summary.downlineLeaders.reduce((sum, l) => sum + l.myShare, 0) : 0;
    const ownCommission = giDashboardData.summary.own?.commission || 0;
    const grandTotal = directTeamEarnings + downlineShare + ownCommission;

    return (
      <div className="space-y-6">
        <DashboardStats
          stats={{
            title: 'General Insurance Overview',
            month: giDashboardData.summary.month,
            metric1Label: 'Total Premium',
            metric1Value: `₹${(giDashboardData.summary.totalPremium || 0).toLocaleString('en-IN')}`,
            metric2Label: 'Total Policies',
            metric2Value: giDashboardData.summary.totalPolicies,
            metric3Label: 'Total Commission',
            metric3Value: `₹${(giDashboardData.summary.totalCommission || 0).toLocaleString('en-IN')}`,
            metric4Label: 'Status',
            metric4Value: 'Active',
            metric4Color: 'text-green-600'
          }}
        />


        {/* Commission Breakdown */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-blue-700 to-indigo-800 px-6 py-4">
            <h3 className="text-white font-semibold text-lg">Commission Breakdown</h3>
          </div>
          <div className="p-6 space-y-8">

            {/* 1. Direct Team Business */}
            <div>
              <h4 className="text-md font-semibold text-gray-800 mb-3 border-b pb-2">Direct Team Business Commission</h4>
              {/* Table implementation similar to before */}
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Advisor</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Their Commission</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Override</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Your Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {giDashboardData.summary.directTeamBreakdown?.map((advisor, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="px-4 py-3">{advisor.name}</td>
                        <td className="px-4 py-3">₹{(advisor.theirCommission || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3">{(uplineRate * 100).toFixed(1)}%</td>
                        <td className="px-4 py-3 font-semibold text-green-600">₹{(advisor.myEarnings || 0).toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 2. Downline Leaders */}
            <div>
              <h4 className="text-md font-semibold text-gray-800 mb-3 border-b pb-2">Downline Leader Commission</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Leader</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Their Commission</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Your Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {giDashboardData.summary.downlineLeaders?.map((leader, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="px-4 py-3">{leader.name}</td>
                        <td className="px-4 py-3">₹{leader.theirCommission.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 font-semibold text-blue-600">₹{leader.myShare.toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 3. Grand Total */}
            <div className="bg-green-50 rounded-lg border border-green-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h5 className="text-lg font-bold text-green-800">Grand Total Commission</h5>
                  <p className="text-sm text-green-600">Team Override + Downline Share</p>
                </div>
                <p className="text-3xl font-bold text-green-700">₹{grandTotal.toLocaleString('en-IN')}</p>
              </div>

              {/* Payment Details - Moved Here */}
              {giDashboardData.summary.status === 'paid' && (
                <div className="mt-4 pt-4 border-t border-green-200">
                  <p className="text-xs text-green-800 uppercase font-semibold mb-2">Payment Details</p>

                  {giDashboardData.summary.payments && giDashboardData.summary.payments.length > 0 ? (
                    <div className="space-y-2">
                      {giDashboardData.summary.payments.map((payment, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm bg-green-50/50 p-2 rounded border border-green-100/50">
                          <div className="flex flex-col">
                            <span className="font-mono text-green-700 font-medium">{payment.transaction_id || 'N/A'}</span>
                            <span className="text-xs text-gray-500">
                              {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-IN', {
                                day: '2-digit', month: 'short', year: 'numeric'
                              }) : 'Date N/A'}
                            </span>
                          </div>
                          <span className="font-bold text-green-800">₹{(payment.amount || 0).toLocaleString('en-IN')}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 italic">No specific transaction details available.</div>
                  )}

                  {giDashboardData.summary.paid_amount > 0 && (
                    <div className="mt-4 text-right pt-2 border-t border-green-200 border-dashed">
                      <span className="text-xs text-green-600 uppercase font-bold mr-2">Total Paid:</span>
                      <span className="text-green-800 font-bold text-lg">₹{giDashboardData.summary.paid_amount.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>


      </div >
    );
  };

  const renderMutualFundTab = () => {
    if (!mfDashboardData) return <LoadingSpinner />;

    // Calculate Grand Total: Team Override + Downline Share + Personal Commission
    const directTeamEarnings = mfDashboardData.summary.directTeam?.myEarnings || 0;
    const downlineShare = mfDashboardData.summary.downlineLeaders ? mfDashboardData.summary.downlineLeaders.reduce((sum, l) => sum + l.myShare, 0) : 0;
    const grandTotal = directTeamEarnings + downlineShare;

    return (
      <div className="space-y-6">
        <DashboardStats
          stats={{
            title: 'Mutual Funds Overview',
            month: mfDashboardData.summary.month,
            metric1Label: 'Total Commission',
            metric1Value: `₹${(mfDashboardData.summary.totalCommission || 0).toLocaleString('en-IN')}`,
            metric2Label: 'Total Entries',
            metric2Value: mfDashboardData.summary.totalEntries
          }}
        />

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-blue-700 to-indigo-800 px-6 py-4">
            <h3 className="text-white font-semibold text-lg">Detailed Commission Breakdown</h3>
          </div>
          <div className="p-6 space-y-8">


            {/* 1. Direct Team Logic */}
            <div>
              <h4 className="text-md font-semibold text-gray-800 mb-3 border-b pb-2">Direct Team Business Commission</h4>
              {/* Table logic similar to above */}
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Advisor</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Their Commission</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Override</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Your Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mfDashboardData.summary.directTeamBreakdown?.map((advisor, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="px-4 py-3">{advisor.name}</td>
                        <td className="px-4 py-3">₹{(advisor.theirCommission || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3">{(mfDashboardData.summary.directTeam?.effectiveRate || 10).toFixed(1)}%</td>
                        <td className="px-4 py-3 font-semibold text-green-600">₹{(advisor.myEarnings || 0).toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 2. Downline Leaders */}
            {/* ... (Implementation is identical to GI logic above) ... */}

            {/* 3. Grand Total */}
            <div className="bg-green-50 rounded-lg border border-green-200 p-6">
              <div className="flex justify-between items-center">
                <div>
                  <h5 className="text-lg font-bold text-green-800">Grand Total Commission</h5>
                  <p className="text-sm text-green-600">Team Override + Downline Share</p>
                </div>
                <p className="text-3xl font-bold text-green-700">₹{grandTotal.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </div>
        </div>
        <MutualFundTab userRole="leader" userId={user.id} />
      </div>
    );
  };



  const renderPostersTab = () => (
    <div className="space-y-6">
      <div className="flex justify-end">
        <div className="w-64">
          <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Category</label>
          <select
            value={selectedPosterCategory}
            onChange={(e) => setSelectedPosterCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="all">All Categories</option>
            {posterCategories.map(category => (
              <option key={category._id} value={category._id}>{category.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      {posterLoading ? (
        <LoadingSpinner />
      ) : posterViewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {posters.map(poster => (
            <div key={poster._id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="aspect-w-3 aspect-h-4 bg-gray-200 relative">
                <img
                  src={`${API_BASE_URL}/posters/${poster._id}/image`}
                  alt={poster.name}
                  className="w-full h-48 object-cover cursor-pointer hover:opacity-80"
                  onClick={() => window.open(`${API_BASE_URL}/posters/${poster._id}/image`, '_blank')}
                />
                {isNewPoster(poster.createdAt) && (
                  <span className="absolute top-2 left-2 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm z-10">
                    NEW
                  </span>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadPersonalizedPoster(poster._id, poster.name, user.name);
                  }}
                  disabled={downloadingPosterId === poster._id}
                  className="absolute top-2 right-2 bg-indigo-600 text-white p-2 rounded-full hover:bg-indigo-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Download with your details"
                >
                  {downloadingPosterId === poster._id ? '...' : <DownloadIcon className="w-4 h-4" />}
                </button>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-1">{poster.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{poster.description || 'No description'}</p>
              </div>
            </div>
          ))}
        </div>
      ) : <p>No posters</p>
      }
    </div>
  );

  return (
    <div className="space-y-8 min-h-screen pb-10">
      {/* Header with Global Filter */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Welcome, {user.name}</h2>
          <p className="text-gray-600">Overview of your team performance and commissions</p>
        </div>

        {/* Global Date Filter - Hide on Profile and Posters */}
        {activeTab !== 'profile' && activeTab !== 'posters' && (
          <div className="flex gap-2">
            <select
              className="px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={selectedMonth.split('-')[1]}
              onChange={handleMonthSelectChange}
            >
              {months.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <select
              className="px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={selectedMonth.split('-')[0]}
              onChange={handleYearChange}
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Navigation Tabs */}
      <nav className="bg-white p-2 rounded-lg shadow-md flex space-x-2 overflow-x-auto sticky top-0 z-20">
        {[
          { id: 'dashboard', label: 'Health Insurance' },
          { id: 'posters', label: 'Marketing Posters' },
          { id: 'general', label: 'General Insurance' },
          { id: 'mutual', label: 'Mutual Funds' },
          { id: 'profile', label: 'My Profile' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${activeTab === tab.id ? 'bg-indigo-600 text-white shadow' : 'text-gray-600 hover:bg-gray-100'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Content Area */}
      <div className="mt-6">
        {activeTab === 'dashboard' && renderHealthTab()}
        {activeTab === 'posters' && renderPostersTab()}
        {activeTab === 'general' && renderGeneralInsuranceTab()}
        {activeTab === 'mutual' && renderMutualFundTab()}
        {activeTab === 'profile' && <ProfileTab user={user} onUpdate={refreshUser} />}
      </div>

    </div>
  );
};

export default LeaderDashboard;